/**
 * PollingTransport (adr-039) — the current transport: translates polling of
 * GET /agent/api/next into clean offered / offer_abandoned / offer_rerouted
 * events. This is the ONE place the racy poll→state translation lives now, and
 * it's unit-tested (pollingTransport.test). SSE/WSS transports will emit the
 * same events, so the client and UI never change.
 *
 * Key resilience rule: a null /next is ambiguous (caller hung up? re-routed?
 * transient blip while media settles?). We never treat it as "gone" on its own
 * — we consult the authoritative interaction state and only fire an event on a
 * definitive answer. Still RINGING_AGENT (or a transient miss) → keep waiting.
 */
import type { AgentEvent, InteractionRef } from "./types";
import type { Transport } from "./agentClient";

export interface PollSource {
  next(): Promise<{ interaction?: InteractionRef | null }>;
  interactionState(interactionId: string): Promise<{ state: string | null; caller_disconnected: boolean }>;
}

export class PollingTransport implements Transport {
  private ringingId: string | null = null;
  private timer: number | null = null;
  private ticking = false;

  constructor(
    private readonly src: PollSource,
    private readonly emit: (e: AgentEvent) => void,
    private readonly intervalMs = 1500,
    private readonly timers: { set: (fn: () => void, ms: number) => number; clear: (id: number) => void } = {
      set: (fn, ms) => globalThis.setInterval(fn, ms) as unknown as number,
      clear: (id) => globalThis.clearInterval(id),
    },
  ) {}

  start() {
    if (this.timer !== null) return;
    void this.tick();
    this.timer = this.timers.set(() => void this.tick(), this.intervalMs);
  }

  stop() {
    if (this.timer !== null) { this.timers.clear(this.timer); this.timer = null; }
    this.ringingId = null;
  }

  /** Exposed for deterministic tests. */
  async tick() {
    if (this.ticking) return;
    this.ticking = true;
    try {
      // Errors are contained here. `start()` calls this as `void this.tick()`,
      // so a rejection used to escape as an unhandled rejection and the poll
      // just went quiet — the agent kept looking AVAILABLE while no offer could
      // ever be seen. Liveness is the client's probe to decide, not this
      // transport's, so a failed poll is simply a poll that found nothing.
      const { interaction } = await this.src.next();
      if (interaction && interaction.interaction_id !== this.ringingId) {
        this.ringingId = interaction.interaction_id;
        this.emit({ type: "offered", interaction });
      } else if (!interaction && this.ringingId) {
        const st = await this.src.interactionState(this.ringingId).catch(() => null);
        if (st?.caller_disconnected) {
          this.ringingId = null;
          this.emit({ type: "offer_abandoned" });
        } else if (st?.state === "QUEUED" || st?.state === "OVERFLOWED") {
          // Genuinely re-routed (RONA / overflow) → back to idle.
          this.ringingId = null;
          this.emit({ type: "offer_rerouted" });
        } else if (st?.state === "WRAP_UP" || st?.state === "COMPLETED") {
          // The call we rang is being wrapped or is over — so it was answered
          // SOMEWHERE ELSE: the desk that accepts stops this transport before
          // its answer round-trip, so it never polls its own call into these
          // states. Seen live (2026-09-23): the same agent signed in on a
          // second (incognito) desk kept showing "Incoming call" after the
          // first had answered and wrapped it. Drop the ring. (CONNECTED stays
          // a keep, above: an in-flight tick during our OWN accept can see it.)
          //
          // And forget the id: kept, it vetoed any later offer carrying the
          // same one — a desk sat on a live campaign call for 47 s because the
          // previous call to that customer had had it.
          this.ringingId = null;
          this.emit({ type: "offer_rerouted" });
        }
        // else: still RINGING_AGENT, a transient miss, OR the agent is answering
        // (CONNECTED / ON_HOLD / …). Keep — the client's own accept() drives the
        // transition; never treat "we answered" as a re-route.
      }
    } catch {
      // swallowed deliberately — see above
    } finally {
      this.ticking = false;
    }
  }
}
